import { useMemo, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronRightSolidV6 } from "@shared/v2/Icomoon";
import formatters from "@shared/formatters";
import { useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import { useWizardContext } from "./WizardContext";

const SOURCE_FIELDS = [
  { key: "pastClientsSphereReferrals", label: "Past Clients, Sphere, Referrals from Past Clients & Sphere" },
  { key: "openHouses", label: "Open Houses" },
  { key: "doorKnocking", label: "Door Knocking" },
  { key: "canceledExpired", label: "Canceled/Expired" },
  { key: "fsbo", label: "FSBO" },
  { key: "circleProspecting", label: "Circle Prospecting" },
  { key: "brivityWebLeadsSignCallsTeamLeads", label: "Brivity/Web Leads, Sign Calls, Team Leads" },
  { key: "agentReferrals", label: "Agent Referrals" },
];

const STYLE_ID = "business-plan-sheet-styles";
const numericGoalValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const PRINT_STYLES = `
  @media not print {
    .business-plan-sheet {
      display: none;
    }
  }

  .business-plan-sheet,
  .business-plan-sheet * {
    box-sizing: border-box;
  }

  .bps-sheet {
    background: #ffffff !important;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    font-size: 12px !important;
    color: #111827 !important;
  }

  .bps-row {
    display: flex;
  }

  .bps-col {
    display: flex;
    flex-direction: column;
  }

  .bps-between {
    justify-content: space-between;
  }

  .bps-center {
    justify-content: center;
  }

  .bps-items-center {
    align-items: center;
  }

  .bps-grow {
    flex: 1;
  }

  .bps-gap-4 {
    gap: 4px;
  }

  .bps-gap-8 {
    gap: 8px;
  }

  .bps-gap-20 {
    gap: 20px;
  }

  .bps-gap-24 {
    gap: 24px;
  }

  .bps-title {
    font-size: 28px;
    font-weight: 700;
  }

  .brivity-theme .bps-title {
    color: #666666 !important;
  }

  .bps-subtitle {
    font-size: 16px;
    font-weight: 400;
  }

  .bps-text-16-bold {
    font-size: 16px;
    font-weight: 700;
  }

  .bps-bold {
    font-weight: 700;
  }

  .bps-normal {
    font-weight: 400;
  }

  .bps-italic {
    font-style: italic;
  }

  .bps-text-center {
    text-align: center;
  }

  .bps-text-right {
    text-align: right !important;
  }

  .bps-logo {
    max-height: 28px !important;
    white-space: nowrap;
  }

  .bps-invert {
    filter: invert(1);
  }

  .bps-section-header {
    padding: 4px;
    background: #59C4C4 !important;
    color: #ffffff !important;
    text-align: center;
    font-weight: 700;
  }

  .bps-section-header-row {
    padding: 4px;
    background: #59C4C4 !important;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 24px;
    text-align: center;
    font-weight: 700;
    color: #ffffff !important;
  }
  .bps-section-header-row > span {
    color: #ffffff !important;
  }

  .place-theme .business-plan-sheet .bps-section-header,
  .place-theme .business-plan-sheet .bps-section-header-row {
    background: #121212 !important;
  }

  .bps-section-header-note {
    color: #ffffff !important;
    font-style: italic;
    font-weight: 400;
  }

  .bps-section-header-note b {
    color: #ffffff !important;
  }

  .bps-card {
    flex: 1;
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 4px;
    border: 1px solid #e5e7eb !important;
  }

  .bps-arrow {
    width: 16px;
    height: 16px;
    color: #9ca3af !important;
  }

  .bps-label-76 {
    width: 76px;
  }

  .bps-label-80 {
    width: 80px;
  }

  .bps-line-86,
  .bps-line-80,
  .bps-line-fill {
    height: 16px;
    border-bottom: 1px solid #6b7280 !important;
  }

  .bps-line-86 {
    width: 86px;
  }

  .bps-line-80 {
    width: 80px;
  }

  .bps-line-fill {
    flex: 1;
  }

  .bps-checkbox {
    margin-top: 0;
  }

  .bps-table {
    width: 100%;
    border-collapse: collapse;
  }

  .bps-table-head-row {
    font-size: 12px;
    color: #6b7280 !important;
    border-top: 1px solid #e5e7eb !important;
    border-bottom: 1px solid #e5e7eb !important;
  }

  .bps-table-head-row th {
    padding: 8px 4px;
    font-weight: 600;
    color: #6b7280 !important;
    text-align: left;
  }

  .bps-th-year {
    width: 84px;
  }

  .bps-th-percent {
    width: 88px;
  }

  .bps-table-body-row td {
    padding: 4px;
  }

  @media print {
    @page {
      size: letter;
      margin: 0;
    }

    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      width: 100% !important;
      font-size: 12px !important;
    }

    body * {
      visibility: hidden;
      height: 0;
    }

    .business-plan-sheet,
    .business-plan-sheet * {
      height: auto;
      visibility: visible;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .business-plan-sheet {
      position: absolute;
      left: 0;
      top: 0;
      width: 100% !important;
      margin: 0 !important;
    }
  }
`;

const BusinessPlanSheet = () => {
  const [stylesAppended, setStylesAppended] = useState(false);
  const currentUser = useCurrentUser();
  const { year, wizardData } = useWizardContext();
  const goals = wizardData?.goals ?? {};

  useEffect(() => {
    document.title =
      `${year}-business-planning-${goals.goalableUser?.person?.firstName ?? ""}-${goals.goalableUser?.person?.lastName ?? ""}`.trim();
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = PRINT_STYLES;
    document.head.appendChild(style);
    setStylesAppended(true);
    return () => {
      document.title = "";
      document.head.removeChild(style);
    };
  }, [goals.goalableUser?.uuid]);

  // Calculate monthly activity goals based on saved yearly goals.
  // Fall back to historical inputs for legacy goal records.
  const monthlyActivities = useMemo(() => {
    const months = 12;
    return {
      calls: Math.round(numericGoalValue(goals.calls ?? goals.callsLastTwelveMonths) / months),
      contacts: Math.round(numericGoalValue(goals.contacts ?? goals.contactsLastTwelveMonths) / months),
      appointmentsSet: Math.round(
        numericGoalValue(goals.appointmentsSet ?? goals.appointmentsSetLastTwelveMonths) / months,
      ),
      appointmentsHeld: Math.round(
        numericGoalValue(goals.appointmentsHeld ?? goals.appointmentsHeldLastTwelveMonths) / months,
      ),
      signings: Math.round(numericGoalValue(goals.signings ?? goals.signingsLastTwelveMonths) / months),
      openHouses: Math.round(numericGoalValue(goals.openHouses) / months),
      doorKnocks: Math.round(numericGoalValue(goals.doorKnocks) / months),
      circleProspectingCalls: Math.round(numericGoalValue(goals.circleProspectingCalls) / months),
      canceledExpiredCalls: Math.round(numericGoalValue(goals.canceledExpiredCalls) / months),
      fsboCalls: Math.round(numericGoalValue(goals.fsboCalls) / months),
    };
  }, [
    goals.calls,
    goals.contacts,
    goals.appointmentsSet,
    goals.appointmentsHeld,
    goals.signings,
    goals.callsLastTwelveMonths,
    goals.contactsLastTwelveMonths,
    goals.appointmentsSetLastTwelveMonths,
    goals.appointmentsHeldLastTwelveMonths,
    goals.signingsLastTwelveMonths,
    goals.openHouses,
    goals.doorKnocks,
    goals.circleProspectingCalls,
    goals.canceledExpiredCalls,
    goals.fsboCalls,
  ]);

  // Calculate unit source percentages
  const sourcePercentages = useMemo(() => {
    const totalUnits = numericGoalValue(goals.closedUnits);
    return SOURCE_FIELDS.reduce((acc, field) => {
      const units = numericGoalValue(goals.unitSources?.[field.key]);
      acc[field.key] = totalUnits > 0 ? Math.round((units / totalUnits) * 100) : 0;
      return acc;
    }, {});
  }, [goals.closedUnits, goals.unitSources]);

  if (typeof document === "undefined" || !stylesAppended) {
    return null;
  }

  return createPortal(
    <div className="business-plan-sheet bps-sheet">
      <div className="bps-row bps-between bps-items-center">
        <div className="bps-title">
          {year} Business Plan{" "}
          <span className="bps-subtitle">
            {`${goals.goalableUser?.person?.firstName ?? ""} ${goals.goalableUser?.person?.lastName ?? ""}`.trim()}
          </span>
        </div>
        <img
          className={`bps-logo ${currentUser.account.isPlace || currentUser.account.isLever ? "bps-invert" : ""}`}
          src={
            currentUser.account.isPlace || currentUser.account.isLever
              ? "/images/PLACE-web-logo-white.png"
              : "/images/logo-with-text.svg"
          }
          alt="logo"
        />
      </div>
      <div className="bps-col bps-gap-8">
        <div className="bps-section-header">Production Goals</div>
        <div className="bps-row bps-gap-20">
          <div className="bps-card">
            <div className="bps-text-16-bold">
              {formatters.currency(numericGoalValue(goals.currentNetIncomeGoal), 0)}
            </div>
            <div>Net Income Goal</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(numericGoalValue(goals.listedUnits))}</div>
            <div>Units Listed</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(numericGoalValue(goals.pendedUnits))}</div>
            <div>Units Pended</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(numericGoalValue(goals.closedUnits))}</div>
            <div>Units Closed</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">
              {formatters.currency(numericGoalValue(goals.closedVolume), 0)}
            </div>
            <div>Closed Volume</div>
          </div>
        </div>
      </div>

      <div className="bps-col bps-gap-8">
        <div className="bps-section-header-row">
          <span>Monthly Activity Goals</span>
        </div>
        <div className="bps-row bps-items-center bps-gap-4">
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.calls)}</div>
            <div>Calls</div>
          </div>
          <ChevronRightSolidV6 className="bps-arrow" />
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.contacts)}</div>
            <div>Contacts</div>
          </div>
          <ChevronRightSolidV6 className="bps-arrow" />
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.appointmentsSet)}</div>
            <div>Appts. Set</div>
          </div>
          <ChevronRightSolidV6 className="bps-arrow" />
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.appointmentsHeld)}</div>
            <div>Appts. Held</div>
          </div>
          <ChevronRightSolidV6 className="bps-arrow" />
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.signings)}</div>
            <div>Signings</div>
          </div>
        </div>
        <div className="bps-row bps-gap-20">
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.openHouses)}</div>
            <div>Open Houses</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.doorKnocks)}</div>
            <div>Door Knocks</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">
              {formatters.number(monthlyActivities.circleProspectingCalls)}
            </div>
            <div>Circle Prosp. Calls</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">
              {formatters.number(monthlyActivities.canceledExpiredCalls)}
            </div>
            <div>Canceled/Exp. Calls</div>
          </div>
          <div className="bps-card">
            <div className="bps-text-16-bold">{formatters.number(monthlyActivities.fsboCalls)}</div>
            <div>FSBO Calls</div>
          </div>
        </div>
      </div>

      <div className="bps-row bps-gap-24">
        <div className="bps-grow bps-col bps-gap-8">
          <div className="bps-section-header">Building My Community</div>
          <div className="bps-row bps-gap-8">
            <div className="bps-label-76">I will make</div>
            <div className="bps-line-86" />
            <div>social media posts monthly</div>
          </div>
          <div className="bps-row bps-gap-8">
            <div className="bps-label-76">I will attend</div>
            <div className="bps-line-86" />
            <div>networking events yearly</div>
          </div>
          <div className="bps-row bps-gap-8">
            <div className="bps-label-76">I will host</div>
            <div className="bps-line-86" />
            <div>client seminars/events yearly</div>
          </div>
        </div>
        <div className="bps-grow bps-col bps-gap-8">
          <div className="bps-section-header">Growth Commitment</div>
          <div className="bps-row bps-gap-8">
            <input className="bps-checkbox" type="checkbox" />
            <div className="bps-label-80">I will receive</div>
            <div className="bps-line-86" />
            <div>{currentUser.account.isPlace ? "PLACE " : ""}certifications</div>
          </div>
          <div className="bps-row bps-gap-8">
            <input className="bps-checkbox" type="checkbox" />
            <div className="bps-label-80">I will attend</div>
            <div className="bps-line-86" />
            <div>learning events/conferences</div>
          </div>
          <div className="bps-row bps-gap-8">
            <input className="bps-checkbox" type="checkbox" />
            <div className="bps-line-fill" />
          </div>
        </div>
      </div>

      {currentUser.account.isPlace && (
        <div className="bps-col bps-gap-8">
          <div className="bps-section-header">PLACE Opportunities | Learn more at our.place.com</div>
          <div className="bps-row bps-gap-24">
            <div className="bps-grow bps-col bps-gap-8">
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Engage in the PLACE Stock Purchase Program</div>
              </div>
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Earn Health Insurance through PLACE</div>
              </div>
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Move up in the Opportunity Model</div>
              </div>
            </div>
            <div className="bps-grow bps-col bps-gap-8">
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Earn Leverage in the Leverage Model</div>
              </div>
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Add to my Revenue Share by recruiting agents to PLACE</div>
              </div>
              <div className="bps-row bps-gap-8">
                <input className="bps-checkbox" type="checkbox" />
                <div>Contribute</div>
                <div className="bps-line-80" />
                <div>loans funded by Envoy</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bps-row bps-gap-24">
        <div className="bps-grow bps-col bps-gap-8">
          <div className="bps-section-header">Where My Closed Units Will Come From</div>
          <table className="bps-table">
            <thead>
              <tr className="bps-table-head-row">
                <th>Source</th>
                <th className="bps-th-year">Units in {year}</th>
                <th className="bps-th-percent">% of Business</th>
              </tr>
            </thead>
            <tbody>
              {SOURCE_FIELDS.map((field) => (
                <tr key={field.key} className="bps-table-body-row">
                  <td>{field.label}</td>
                  <td className="bps-text-center">
                    {formatters.number(goals.unitSources?.[field.key] || 0)}
                  </td>
                  <td className="bps-text-right">
                    {sourcePercentages[field.key] === 0 ? "—" : `${sourcePercentages[field.key]}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bps-grow bps-col bps-gap-8">
          <div className="bps-section-header">Production Stats, Splits, and Caps</div>
          <table className="bps-table">
            <thead>
              <tr className="bps-table-head-row">
                <th>Stat</th>
                <th className="bps-text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bps-table-body-row">
                <td>Avg. Sale Price</td>
                <td className="bps-text-right">{formatters.currency(goals.avgSalePrice, 0)}</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Avg. Commission</td>
                <td className="bps-text-right">{formatters.number(goals.avgCommission, 2)}%</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Pending to Closed Ratio</td>
                <td className="bps-text-right">{formatters.number(goals.pendingToCloseRatio, 2)}%</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Seller to Total Closings Ratio</td>
                <td className="bps-text-right">{formatters.number(goals.sellerToTotalClosingsRatio, 2)}%</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Brokerage Split</td>
                <td className="bps-text-right">{formatters.number(goals.brokerageSplitPercentage, 2)}%</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Brokerage Cap</td>
                <td className="bps-text-right">{formatters.currency(goals.brokerageCap, 0)}</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Royalty Split</td>
                <td className="bps-text-right">{formatters.number(goals.royaltyPercentage, 2)}%</td>
              </tr>
              <tr className="bps-table-body-row">
                <td>Royalty Cap</td>
                <td className="bps-text-right">{formatters.currency(goals.royaltyCap, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default BusinessPlanSheet;
